/* eslint-disable arrow-body-style */
import { Relation, ShortcutActionType } from '@intelligence/components/GenreSelect/enums';
import { MAX_SUB_GENRES_OF_RELATION_AND } from '@intelligence/components/GenreSelect/constants';

import type { IGenreCheckboxOption } from '@intelligence/components/GenreSelect/types';
import type { ISubGenreCheckboxOptionsMapper } from '@intelligence/components/GenreSelect/reducers/types';

/**
 * 修改主品类relation时，副品类的map映射方法map
 * 在map映射副品类Checkbox的Options集合时，map方法内部会有4中情况，需要多个if判断，所以加了这个map映射方法的map，把map方法内的if判断抽出来单独成为一个map方法
 * Available: 返回取消副品类disabled状态的map映射方法
 * DisabledUnChecked: 返回禁用当前没勾选的副品类的map映射方法
 * DefaultCheckedAndDisabled: 返回勾选前MAX_SUB_GENRES_OF_RELATION_AND个，禁用掉前MAX_SUB_GENRES_OF_RELATION_AND个之后的map映射方法
 */
const RelationChangeSubGenreCheckboxOptionsMappers: {
  [key in 'Available' | 'DisabledUnChecked' | 'DefaultCheckedAndDisabled']: ISubGenreCheckboxOptionsMapper;
} = {
  // 取消禁用状态
  Available: (subGenreCheckboxOption: IGenreCheckboxOption): IGenreCheckboxOption => {
    return { ...subGenreCheckboxOption, disabled: false };
  },
  // 禁用没勾选的
  DisabledUnChecked: (subGenreCheckboxOption: IGenreCheckboxOption): IGenreCheckboxOption => {
    return { ...subGenreCheckboxOption, disabled: !subGenreCheckboxOption?.checked };
  },
  // 勾选前MAX_SUB_GENRES_OF_RELATION_AND个，禁用掉前MAX_SUB_GENRES_OF_RELATION_AND个之后的
  DefaultCheckedAndDisabled: (
    subGenreCheckboxOption: IGenreCheckboxOption,
    subGenreCheckboxOptionIdx: number,
  ): IGenreCheckboxOption => {
    return {
      ...subGenreCheckboxOption,
      disabled: subGenreCheckboxOptionIdx >= MAX_SUB_GENRES_OF_RELATION_AND,
      checked: subGenreCheckboxOptionIdx < MAX_SUB_GENRES_OF_RELATION_AND,
    };
  },
};

/**
 * 获取副品类Checkbox的Options集合的map映射方法，用于修改主品类relation时，map映射修改主品类下的副品类Checkbox的Options集合
 * @param params.relation 主品类relation（修改后的）
 * @param params.subGenreCheckboxOptions 副品类Checkbox的Options集合
 * @returns 副品类Checkbox的Options集合的map映射方法
 */
export const getRelationChangeSourceGenreCheckboxOptionChildrenMapper = (params: {
  relation: Relation;
  subGenreCheckboxOptions: IGenreCheckboxOption[];
}): ISubGenreCheckboxOptionsMapper => {
  const { relation, subGenreCheckboxOptions } = params || {};
  const subGenreCheckboxOptionsChecked = subGenreCheckboxOptions?.filter((item) => item?.checked);
  const subGenreCheckboxOptionsCheckedLength = subGenreCheckboxOptionsChecked?.length || 0;
  if (relation === Relation.Or) return RelationChangeSubGenreCheckboxOptionsMappers.Available;
  if (subGenreCheckboxOptionsCheckedLength < MAX_SUB_GENRES_OF_RELATION_AND)
    return RelationChangeSubGenreCheckboxOptionsMappers.Available;
  if (subGenreCheckboxOptionsCheckedLength === MAX_SUB_GENRES_OF_RELATION_AND)
    return RelationChangeSubGenreCheckboxOptionsMappers.DisabledUnChecked;
  return RelationChangeSubGenreCheckboxOptionsMappers.DefaultCheckedAndDisabled;
};

type IShortcutsSubGenreCheckboxOptionsMapperBuilder = (params: {
  // 快捷勾选按钮是否已勾选
  shortcutChecked: boolean;
  // 快捷勾选按钮value
  shortcutValue: string;
  // 当前主品类relation
  relation: Relation;
  // 当前主品类下已勾选的副品类和当前主品类下即将勾选的副品类的总数量
  length: number;
}) => ISubGenreCheckboxOptionsMapper;

const ShortcutsSubGenreCheckboxOptionsMapperBuilders: {
  [key in ShortcutActionType]: IShortcutsSubGenreCheckboxOptionsMapperBuilder;
} = {
  // 快捷品类勾选为组合模式（即点击快捷品类勾选按钮时，会在当前已勾选主、副品类的基础上，勾选快捷品类按钮对应的主、副品类）
  [ShortcutActionType.Compose]: (params) => (subGenreCheckboxOption) => {
    const { shortcutChecked, shortcutValue, relation, length } = params || {};
    const isMatch = shortcutValue === subGenreCheckboxOption?.value;
    if (isMatch && shortcutChecked) {
      return {
        ...subGenreCheckboxOption,
        disabled: false,
        checked: false,
      };
    }
    /**
     * 满足以下2个条件中任意1个时，当前副品类的disabled状态为false
     * 1、当前主品类的relation为or
     * 2、当前主品类下已勾选的副品类和当前主品类下即将勾选的副品类的总数量小于MAX_SUB_GENRES_OF_RELATION_AND配置的数值
     */
    if (relation === Relation.Or || length < MAX_SUB_GENRES_OF_RELATION_AND) {
      return {
        ...subGenreCheckboxOption,
        disabled: false,
        checked: isMatch || subGenreCheckboxOption?.checked,
      };
    }
    // 当前主品类下已勾选的副品类和当前主品类下即将勾选的副品类的总数量等于MAX_SUB_GENRES_OF_RELATION_AND配置的数值时，禁用没勾选的副品类
    if (length === MAX_SUB_GENRES_OF_RELATION_AND) {
      const isChecked = isMatch || subGenreCheckboxOption?.checked;
      return {
        ...subGenreCheckboxOption,
        disabled: !isChecked,
        checked: isChecked,
      };
    }
    // 当前主品类下已勾选的副品类和当前主品类下即将勾选的副品类的总数量大于MAX_SUB_GENRES_OF_RELATION_AND配置的数值时，仅勾选匹配快捷按钮的副品类，其他不匹配的副品类不勾选并且不禁用
    return {
      ...subGenreCheckboxOption,
      disabled: false,
      checked: isMatch,
    };
  },
  // 快捷品类勾选为替换模式（即点击快捷品类勾选按钮时，会把当前已勾选主、副品类清空，然后再勾选快捷品类按钮对应的主、副品类）
  [ShortcutActionType.Replace]: (params) => (subGenreCheckboxOption) => {
    const shortcutChecked = params?.shortcutChecked || false;
    const isMatch = params?.shortcutValue === subGenreCheckboxOption?.value;
    if (isMatch && shortcutChecked) {
      return {
        ...subGenreCheckboxOption,
        disabled: false,
        checked: false,
      };
    }
    return {
      ...subGenreCheckboxOption,
      // 目前一个快捷品类勾选按钮只对应一个副品类（同一个主品类下的副品类集合），所以disabled直接重置为false
      disabled: false,
      checked: isMatch,
    };
  },
};

export const getShortcutsSourceGenreCheckboxOptionChildrenMapperBuilder = (
  shortcutActionType: ShortcutActionType,
): IShortcutsSubGenreCheckboxOptionsMapperBuilder => {
  return (
    ShortcutsSubGenreCheckboxOptionsMapperBuilders[shortcutActionType] ||
    ShortcutsSubGenreCheckboxOptionsMapperBuilders[ShortcutActionType.Compose]
  );
};
